import { useEffect, useState } from 'react';

const sToMs = (s: number) => s * 1000;
const mToMs = (m: number) => sToMs(m * 60);

export enum DeviceGeolocationStatus {
    NotRequested = 'NotRequested',
    Unavailable = 'Unavailable',
    Loading = 'Loading',
    Available = 'Available',
}

type RequestGeolocationOnSuccess = (loc: GeolocationPosition) => void;
type RequestGeolocationOnError = (err: GeolocationPositionError) => void;

export const useDeviceGeolocation = () => {
    const [status, setStatus] = useState<DeviceGeolocationStatus>(
        DeviceGeolocationStatus.NotRequested
    );
    const [location, setLocation] = useState<GeolocationCoordinates | null>(
        null
    );
    const [hasLocation, setHasLocation] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        setHasLocation(
            status === DeviceGeolocationStatus.Available && !!location
        );
        setIsLoading(status === DeviceGeolocationStatus.Loading);
    }, [status, location]);

    const requestGeolocation = (
        onSuccess: RequestGeolocationOnSuccess = () => void 0,
        onError: RequestGeolocationOnError = () => void 0
    ) => {
        setStatus(DeviceGeolocationStatus.Loading);
        if (!window.navigator?.geolocation) {
            setStatus(DeviceGeolocationStatus.Unavailable);
            return;
        }
        window.navigator.geolocation.getCurrentPosition(
            (loc) => {
                if (loc) {
                    setLocation(loc.coords);
                    setStatus(DeviceGeolocationStatus.Available);
                }
                onSuccess(loc);
            },
            (err) => {
                setStatus(DeviceGeolocationStatus.Unavailable);
                onError(err);
            },
            { maximumAge: mToMs(5), timeout: sToMs(10) }
        );
    };

    return {
        status,
        location,
        hasLocation,
        isLoading,
        requestGeolocation,
    };
};
